import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient, HttpHeaders, HttpResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, catchError } from "rxjs/operators";
import { TranslateService } from '@ngx-translate/core';
//import 'rxjs/Rx';
//import 'rxjs/add/observable/throw';

import { ChartConfigBase } from '../models/chart-config-base';
import { HighChartDataBase } from '../models/high-chart-data-base';
import { BoxPlotHighChartData } from "../models/boxplot-high-chart-data";
import { ColumnHighChartData } from '../models/column-high-chart-data';
import { ColumnRangeHighChartData } from '../models/columnrange-high-chart-data';
import { DateTimeHighChartData } from '../models/datetime-high-chart-data';
import { FunnelHighChartData } from '../models/funnel-high-chart-data';
import { LineHighChartData } from '../models/line-high-chart-data';
import { LineCategoryHighChartData } from '../models/line-category-high-chart-data';
import { PieHighChartData } from '../models/pie-high-chart-data';
import { PolarHighChartData } from "../models/polar-high-chart-data";
import { ClickNotifier } from '../models/click-notifier';

import * as Highcharts from 'highcharts';


@Injectable({ providedIn: 'root' })
export class XmlReportDataService {

    constructor(
        private http: HttpClient,
        private location: Location,
        private translate: TranslateService
    ) { }

    public currentGroupings = new BehaviorSubject<string[]>(null);

    private setHighChartDefaults(): void {
        var options = {
            colors: ['#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b', '#91e8e1'],
            exporting: {
                width: 1200,
                sourceWidth: 1200,
                sourceHeight: 500,
                fallbackToExportServer: false
            },
            lang: {
                contextButtonTitle: this.translate.instant('xml-report.chart.highcharts.contextButtonTitle'),
                decimalPoint: this.translate.instant('xml-report.chart.highcharts.decimalPoint'),
                downloadJPEG: this.translate.instant('xml-report.chart.highcharts.downloadJPEG'),
                downloadPDF: this.translate.instant('xml-report.chart.highcharts.downloadPDF'),
                downloadPNG: this.translate.instant('xml-report.chart.highcharts.downloadPNG'),
                downloadSVG: this.translate.instant('xml-report.chart.highcharts.downloadSVG'),
                drillUpText: this.translate.instant('xml-report.chart.highcharts.drillUpText', {serie: '{series.name}'}),
                invalidDate: this.translate.instant('xml-report.chart.highcharts.invalidDate'),
                loading: this.translate.instant('xml-report.chart.highcharts.loading'),
                months: [this.translate.instant('months.01'),
                        this.translate.instant('months.02'),
                        this.translate.instant('months.03'),
                        this.translate.instant('months.04'),
                        this.translate.instant('months.05'),
                        this.translate.instant('months.06'),
                        this.translate.instant('months.07'),
                        this.translate.instant('months.08'),
                        this.translate.instant('months.09'),
                        this.translate.instant('months.10'),
                        this.translate.instant('months.11'),
                        this.translate.instant('months.12')],
                noData: this.translate.instant('xml-report.chart.highcharts.noData'),
                numericSymbols: null,
                printChart: this.translate.instant('xml-report.chart.highcharts.printChart'),
                resetZoom: this.translate.instant('xml-report.chart.highcharts.resetZoom'),
                resetZoomTitle: this.translate.instant('xml-report.chart.highcharts.resetZoomTitle'),
                shortMonths: [this.translate.instant('months.short.01'),
                            this.translate.instant('months.short.02'),
                            this.translate.instant('months.short.03'),
                            this.translate.instant('months.short.04'),
                            this.translate.instant('months.short.05'),
                            this.translate.instant('months.short.06'),
                            this.translate.instant('months.short.07'),
                            this.translate.instant('months.short.08'),
                            this.translate.instant('months.short.09'),
                            this.translate.instant('months.short.10'),
                            this.translate.instant('months.short.11'),
                            this.translate.instant('months.short.12')],
                thousandsSep: this.translate.instant('xml-report.chart.highcharts.thousandsSep'),
                weekdays: [this.translate.instant('weekdays.07'),
                        this.translate.instant('weekdays.01'),
                        this.translate.instant('weekdays.02'),
                        this.translate.instant('weekdays.03'),
                        this.translate.instant('weekdays.04'),
                        this.translate.instant('weekdays.05'),
                        this.translate.instant('weekdays.06')]
            }
        };

        Highcharts.setOptions(options);
    }

    public getExcelTable(config: ChartConfigBase): Observable<Blob> {
        let configParams: HttpParams = this.getConfigParams(config);
        return this.http.post(config.apiBaseUrl + config.tableUrl, configParams, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                responseType: 'blob'
            })
            .pipe(map((response) => {
                return response;
            }));
    }

    public getHighChartData(config: ChartConfigBase, clickNotifier?: ClickNotifier): Observable<HighChartDataBase> {
        try {
            this.setHighChartDefaults();
            let configParams: HttpParams = this.getConfigParams(config);
            const httpOptions = {
                headers: new HttpHeaders({
                    contentType: 'application/x-www-form-urlencoded'
                })
            };
            return this.http.post(config.apiBaseUrl + config.highChartUrl, configParams, httpOptions)
                .pipe(map((response: any) => {
                    switch (response.ChartType) {
                        case "column":
                        case "stacked":
                            let columnData = new ColumnHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(columnData.groupings);
                            return columnData;
                        case "boxplot":
                            let boxplotData = new BoxPlotHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(boxplotData.groupings);
                            return boxplotData;
                        case "datetime":
                            let dateTimeData = new DateTimeHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(dateTimeData.groupings);
                            return dateTimeData;
                        case "funnel":
                        case "spline":
                            response.ChartType = "funnel";
                            let funnelData = new FunnelHighChartData(this.translate, response, config.chartWidth, config.chartHeight);
                            this.currentGroupings.next(funnelData.groupings);
                            return funnelData;
                        case "lineCategory":
                            let lineCategoryData = new LineCategoryHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(lineCategoryData.groupings);
                            return lineCategoryData;
                        case "line":
                        case "point":
                            let lineData = new LineHighChartData(this.translate, response, config.chartWidth, config.chartHeight);
                            this.currentGroupings.next(lineData.groupings);
                            return lineData;
                        case "pie":
                            let pieData = new PieHighChartData(this.translate, response, config.chartWidth, config.chartHeight);
                            this.currentGroupings.next(pieData.groupings);
                            return pieData;
                        case "rangecolumn":
                            let columnRangeData = new ColumnRangeHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(columnRangeData.groupings);
                            return columnRangeData;
                        case "radar":
                            let polarData = new PolarHighChartData(this.translate, response, config.chartWidth, config.chartHeight, clickNotifier);
                            this.currentGroupings.next(polarData.groupings);
                            return polarData;
                        default:
                            this.translate.get('xml-report.chart.not-implemented-msg').subscribe((result: string) => {
                                throw (result);
                            });
                        }
                        return null;
                    }))
                    .pipe(catchError(err => this.handleServerApiError(err)));

        } catch (e) {
            return throwError(() => new Error(e.Message));
        }
    }

    private getConfigParams(config: ChartConfigBase): HttpParams {
        let params = new HttpParams();
        Object.keys(config).map((key: string) => {
            //Hämta alla egenskaper på config och sätt som params i requesten till backend
            if (config[key]) {
                if (config[key].constructor === Array) {
                    let arr = config[key] as any[];
                    arr.forEach((value, index) => {
                        params = params.set(key + `[${index}]`, value.toString());
                    });
                } else {
                    let value = config[key].toString();
                    params = params.set(key, value);
                }
            }
        });
        return params;
    }

    protected handleServerApiError(errorResponse: HttpResponse<any>): Observable<HighChartDataBase> {
        return throwError(() => new Error(errorResponse['error']));
    }
}
